// Event Site settings — manages the public event site at
// events.attend.vip/{org}/{event} (Astro SSR). Ported from the admin SPA's
// EventSitePage.tsx (the SitePanels fn), rebound onto the Console stack:
// aaa-2 settings primitives, core/event-site.ts data layer, plum/Fluent
// styling, container-query responsive.
//
// The site IS the event's splash page row. This panel edits its chrome:
//   • public website hero image (events.coverImageUrl)
//   • SEO title/description
//   • layout + registration form visibility + per-field toggles
//   • register heading/subheading + CTA label
//   • footer links (terms/privacy/support)
// The site THEME (preset + color tokens + fonts) is edited on the Branding
// page (Appearance group) alongside the app theme; update-site merges
// settings shallowly, so saves from here never clobber it.
//
// Only meaningful for public-registration events (the public site only exists
// when registrationMethod === "public_page"); we render the panel regardless
// and note when it applies, so organizers can prep the site before flipping
// registration public.

import { useEffect, useMemo, useState } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ArrowUpRight, Globe, Pencil } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import { ImageUploadField } from "@/components/settings/ImageUploadField"
import { eventSettingsQueryOptions, patchEvent } from "@/core/settings"
import {
  createSplashPage,
  eventPagesQueryOptions,
  publicEventSiteUrl,
  siteRowQueryOptions,
  updateEventSite,
  type SiteFormFieldFlags,
  type SiteLinks,
  type SiteSettings,
} from "@/core/event-site"
import {
  Card,
  Checkbox,
  CopyLinkBox,
  Field,
  PrimaryButton,
  Select,
  SectionPage,
  SectionPageFooter,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/site")({
  component: SiteView,
})

function SiteView() {
  const { eventId, orgSlug, eventSlug } = useActiveEvent()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const editContent = (pageId: string) =>
    void navigate({
      to: "/$orgSlug/$eventSlug/p/$pageId",
      params: { orgSlug: orgSlug!, eventSlug: eventSlug!, pageId },
    })

  const eventQ = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const pagesQ = useQuery({ ...eventPagesQueryOptions(eventId ?? ""), enabled: !!eventId })

  const splash = useMemo(
    () => (pagesQ.data ?? []).find((p) => p.kind === "splash" || p.slug === "splash") ?? null,
    [pagesQ.data],
  )

  const method = eventQ.data?.registrationMethod ?? null
  const isPublic = method === "public_page"
  const liveUrl = orgSlug && eventSlug ? publicEventSiteUrl(orgSlug, eventSlug) : null

  const createSplash = useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error("Not ready")
      return createSplashPage(eventId, eventQ.data?.name ?? "Welcome")
    },
    onSuccess: (res) => {
      toast.success("Event site created")
      void qc.invalidateQueries({ queryKey: ["event-pages", eventId] })
      // Drop straight into the content editor for the new landing page.
      if (res?.page?.id) editContent(res.page.id)
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to create the site"),
  })

  return (
    <SectionPage
      title="Event site"
      description="The public event page at events.attend.vip — hero, SEO, layout, and the registration form. Theme lives on the Branding page."
      actions={
        splash && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => editContent(splash.id)}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold text-white shadow-fluent-sm transition"
              style={{ background: "var(--accent)" }}
            >
              <Pencil className="h-3.5 w-3.5" /> Edit content
            </button>
            {liveUrl && (
              <a
                href={liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-plum-200/70 bg-white px-3.5 text-[13px] font-semibold text-plum-800 transition hover:bg-plum-100/60 dark:border-white/10 dark:bg-white/5 dark:text-plum-100 dark:hover:bg-white/10"
              >
                Visit site <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )
      }
    >
      {!isPublic && eventQ.data && (
        <Card>
          <div className="flex items-start gap-3">
            <Globe className="mt-0.5 h-4 w-4 shrink-0 text-plum-700/55 dark:text-plum-200/40" />
            <p className="text-[13px] text-plum-700/65 dark:text-plum-200/50">
              The public site only goes live when{" "}
              <Link
                to="/$orgSlug/$eventSlug/settings/registration"
                params={{ orgSlug: orgSlug!, eventSlug: eventSlug! }}
                className="font-semibold text-[var(--accent)] underline-offset-2 hover:underline"
              >
                Registration
              </Link>{" "}
              is set to <span className="font-semibold">Public page</span>. You can still set
              everything up here — it applies the moment you flip registration public.
            </p>
          </div>
        </Card>
      )}

      {liveUrl && (
        <Card title="Registration link">
          <CopyLinkBox url={liveUrl} ariaLabel="Copy registration link" />
        </Card>
      )}

      {pagesQ.isLoading || eventQ.isLoading ? (
        <SitePanelsSkeleton />
      ) : splash && eventQ.data ? (
        <SitePanels
          splashId={splash.id}
          eventId={eventQ.data.id}
          coverImageUrl={eventQ.data.coverImageUrl}
          isPublic={isPublic}
        />
      ) : (
        <Card title="Create your event site">
          <p className="text-[13px] text-plum-700/65 dark:text-plum-200/50">
            A collaborative page your team edits together — published to the public URL above
            with your registration form built in.
          </p>
          <PrimaryButton
            className="mt-1"
            onClick={() => createSplash.mutate()}
            disabled={createSplash.isPending}
          >
            <Globe className="h-4 w-4" />
            {createSplash.isPending ? "Creating…" : "Create event site"}
          </PrimaryButton>
        </Card>
      )}
    </SectionPage>
  )
}

// ── skeleton — mirrors the SitePanels stacked-card form ───────────────────────

function CardSkeleton({ rows }: { rows: number }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-plum-200/60 bg-white shadow-fluent-sm dark:border-white/10 dark:bg-white/[0.02]">
      <header className="border-b border-plum-200/50 px-5 py-4 dark:border-white/10">
        <div className="h-4 w-40 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
      </header>
      <div className="space-y-4 px-5 py-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-3 w-28 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
            <div className="h-9 w-full animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
          </div>
        ))}
      </div>
    </section>
  )
}

function SitePanelsSkeleton() {
  // Same five titled cards as SitePanels (Hero / Search & sharing /
  // Social sharing & icons / Layout & registration / Footer links) + the
  // save button bar.
  return (
    <>
      <CardSkeleton rows={1} />
      <CardSkeleton rows={2} />
      <CardSkeleton rows={3} />
      <CardSkeleton rows={3} />
      <CardSkeleton rows={3} />
      <div className="h-9 w-full animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
    </>
  )
}

// ── the panels (ported from admin SitePanels) ─────────────────────────────────

function SitePanels({
  splashId,
  eventId,
  coverImageUrl,
  isPublic,
}: {
  splashId: string
  eventId: string
  coverImageUrl: string | null
  isPublic: boolean
}) {
  const qc = useQueryClient()
  const siteQ = useQuery(siteRowQueryOptions(splashId))

  const [seoTitle, setSeoTitle] = useState("")
  const [seoDescription, setSeoDescription] = useState("")
  const [layout, setLayout] = useState<"single" | "form-sidebar">("form-sidebar")
  const [showForm, setShowForm] = useState(true)
  const [ctaLabel, setCtaLabel] = useState("")
  const [registerTitle, setRegisterTitle] = useState("")
  const [registerSubtitle, setRegisterSubtitle] = useState("")
  const [formFields, setFormFields] = useState<SiteFormFieldFlags>({})
  const [links, setLinks] = useState<Record<keyof SiteLinks, string>>({
    terms: "",
    privacy: "",
    support: "",
  })
  const [heroUrl, setHeroUrl] = useState(coverImageUrl ?? "")
  const [ogImageUrl, setOgImageUrl] = useState("")
  const [faviconUrl, setFaviconUrl] = useState("")
  const [siteName, setSiteName] = useState("")
  const [twitterHandle, setTwitterHandle] = useState("")
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if (hydrated || !siteQ.data) return
    let settings: SiteSettings = {}
    try {
      settings = siteQ.data.siteSettings
        ? (JSON.parse(siteQ.data.siteSettings) as SiteSettings)
        : {}
    } catch {
      settings = {}
    }
    setSeoTitle(siteQ.data.seoTitle ?? "")
    setSeoDescription(siteQ.data.seoDescription ?? "")
    // Mirror the renderer's default (apps/events index.astro): form-sidebar
    // unless the organizer explicitly chose single — otherwise the first save
    // from this page would silently flip an unset site to single-column.
    setLayout(settings.layout === "single" ? "single" : "form-sidebar")
    setShowForm(settings.showForm !== false)
    setCtaLabel(settings.ctaLabel ?? "")
    setRegisterTitle(settings.registerTitle ?? "")
    setRegisterSubtitle(settings.registerSubtitle ?? "")
    setFormFields(settings.formFields ?? {})
    setLinks({
      terms: settings.links?.terms ?? "",
      privacy: settings.links?.privacy ?? "",
      support: settings.links?.support ?? "",
    })
    setOgImageUrl(settings.social?.ogImageUrl ?? "")
    setFaviconUrl(settings.social?.faviconUrl ?? "")
    setSiteName(settings.social?.siteName ?? "")
    setTwitterHandle(settings.social?.twitterHandle ?? "")
    setHydrated(true)
  }, [siteQ.data, hydrated])

  const saveSite = useMutation({
    mutationFn: async () => {
      // Hero rides the events row (branding pattern); everything else rides
      // the splash row via update-site.
      const trimmedHero = heroUrl.trim()
      if (trimmedHero !== (coverImageUrl ?? "")) {
        await patchEvent(eventId, { coverImageUrl: trimmedHero === "" ? null : trimmedHero })
      }
      return updateEventSite({
        pageId: splashId,
        seoTitle: seoTitle.trim() === "" ? null : seoTitle.trim(),
        seoDescription: seoDescription.trim() === "" ? null : seoDescription.trim(),
        settings: {
          layout,
          showForm,
          formFields,
          ...(registerTitle.trim() ? { registerTitle: registerTitle.trim() } : {}),
          ...(registerSubtitle.trim() ? { registerSubtitle: registerSubtitle.trim() } : {}),
          ...(ctaLabel.trim() ? { ctaLabel: ctaLabel.trim() } : {}),
          // theme is deliberately absent — the Branding page owns it, and
          // update-site merges settings shallowly so it survives this save.
          ...(() => {
            const trimmed = Object.fromEntries(
              Object.entries(links)
                .map(([k, v]) => [k, v.trim()] as const)
                .filter(([, v]) => v !== ""),
            )
            return Object.keys(trimmed).length > 0 ? { links: trimmed } : {}
          })(),
          ...(() => {
            const social = {
              ...(ogImageUrl.trim() ? { ogImageUrl: ogImageUrl.trim() } : {}),
              ...(faviconUrl.trim() ? { faviconUrl: faviconUrl.trim() } : {}),
              ...(siteName.trim() ? { siteName: siteName.trim() } : {}),
              ...(twitterHandle.trim()
                ? { twitterHandle: twitterHandle.trim().replace(/^@/, "") }
                : {}),
            }
            return Object.keys(social).length > 0 ? { social } : {}
          })(),
        },
      })
    },
    onSuccess: () => {
      toast.success("Site settings saved", {
        description: "Live within moments — no republish needed.",
      })
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
      void qc.invalidateQueries({ queryKey: ["event-site-row", splashId] })
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save site settings"),
  })

  if (siteQ.isLoading) {
    // Mirror the real stacked-card form: titled cards each with a few
    // labelled (h-9 input) field rows, then the full-width save button.
    return <SitePanelsSkeleton />
  }

  return (
    <>
      <Card title="Hero image">
        <ImageUploadField
          eventId={eventId}
          label="Image"
          hint="Shown as a full-width banner. Upload or paste a hosted URL."
          preview="banner"
          value={heroUrl}
          onChange={setHeroUrl}
        />
      </Card>

      <Card title="Search & sharing">
        <Field label="SEO title">
          <TextInput
            maxLength={70}
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
          />
        </Field>
        <Field label="SEO description">
          <TextInput
            maxLength={200}
            value={seoDescription}
            onChange={(e) => setSeoDescription(e.target.value)}
          />
        </Field>
      </Card>

      <Card
        title="Social sharing & icons"
        description="How the page looks when shared (OpenGraph / Twitter) and the favicon in the browser tab. Each falls back to the hero image or your org logo when left blank."
      >
        <ImageUploadField
          eventId={eventId}
          label="Share image (OpenGraph)"
          hint="Shown on social cards. 1200×630 recommended. Falls back to the hero image."
          preview="wide"
          value={ogImageUrl}
          onChange={setOgImageUrl}
        />
        <ImageUploadField
          eventId={eventId}
          label="Favicon"
          hint="Browser-tab icon. Square PNG or SVG. Falls back to your org logo."
          preview="icon"
          accept="image/png,image/svg+xml,image/x-icon,image/*"
          value={faviconUrl}
          onChange={setFaviconUrl}
        />
        <div className="grid gap-4 @[480px]:grid-cols-2">
          <Field label="Site name" hint="Shown as og:site_name on shared links.">
            <TextInput
              maxLength={100}
              placeholder="Your organization"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
            />
          </Field>
          <Field label="Twitter / X handle" hint="For twitter:site attribution.">
            <TextInput
              maxLength={16}
              placeholder="@yourbrand"
              value={twitterHandle}
              onChange={(e) => setTwitterHandle(e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card title="Layout & registration">
        <Field label="Layout">
          <Select value={layout} onChange={(e) => setLayout(e.target.value as typeof layout)}>
            <option value="single">Single column</option>
            <option value="form-sidebar">Form sidebar</option>
          </Select>
        </Field>

        {isPublic && (
          <>
            <Checkbox
              checked={showForm}
              onChange={setShowForm}
              label="Show the registration form"
            />
            <Field label="Form asks for">
              {/* Checkbox renders inline-flex, so give the row real horizontal
                  gaps (space-y-* alone leaves inline labels touching). */}
              <div className="flex flex-wrap gap-x-6 gap-y-2.5 rounded-xl border border-plum-200/60 px-3.5 py-3 dark:border-white/10">
                {(
                  [
                    ["title", "Title"],
                    ["phone", "Mobile number + SMS opt-in"],
                    ["company", "Company"],
                    ["note", "Note to the organizers"],
                  ] as Array<[keyof SiteFormFieldFlags, string]>
                ).map(([key, label]) => (
                  <Checkbox
                    key={key}
                    checked={formFields[key] !== false}
                    onChange={(next) => setFormFields((prev) => ({ ...prev, [key]: next }))}
                    label={label}
                  />
                ))}
              </div>
            </Field>
          </>
        )}

        <div className="grid gap-4 @[480px]:grid-cols-2">
          <Field label="Register heading">
            <TextInput
              maxLength={80}
              placeholder="Register"
              value={registerTitle}
              onChange={(e) => setRegisterTitle(e.target.value)}
            />
          </Field>
          <Field label="Register subheading">
            <TextInput
              maxLength={200}
              value={registerSubtitle}
              onChange={(e) => setRegisterSubtitle(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Button label">
          <TextInput
            placeholder="Request to join"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
          />
        </Field>
      </Card>

      <Card title="Footer links" description="Shown in the public site footer. Leave blank to hide a link; https or mailto only.">
        <div className="grid gap-4 @[560px]:grid-cols-3">
          {(
            [
              ["terms", "Terms", "https://…"],
              ["privacy", "Privacy", "https://…"],
              ["support", "Support", "https://… or mailto:…"],
            ] as Array<[keyof SiteLinks, string, string]>
          ).map(([key, label, placeholder]) => (
            <Field key={key} label={label}>
              <TextInput
                type="url"
                maxLength={500}
                placeholder={placeholder}
                value={links[key]}
                onChange={(e) => setLinks((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            </Field>
          ))}
        </div>
      </Card>

      <SectionPageFooter>
        <PrimaryButton
          className="w-auto"
          onClick={() => saveSite.mutate()}
          disabled={saveSite.isPending}
        >
          {saveSite.isPending ? "Saving…" : "Save site settings"}
        </PrimaryButton>
      </SectionPageFooter>
    </>
  )
}
